import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/authService';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            const token = sessionStorage.getItem('token');

            if (!token) {
                // No token at all — user is logged out
                setLoading(false);
                return;
            }

            try {
                // ✅ Always verify with the backend on boot.
                // This prevents stale localStorage tokens from granting access
                // and catches role changes / account deactivations immediately.
                const response = await api.get('/auth/verify');

                if (response.data.success) {
                    const freshUser = response.data.data;
                    setUser(freshUser);
                    sessionStorage.setItem('user', JSON.stringify(freshUser));
                } else {
                    // Server rejected the token — clear everything
                    authService.logout();
                    setUser(null);
                }
            } catch (error) {
                // 401 / network error — clear auth state
                console.warn('[AuthContext] Token verification failed:', error?.response?.status, error?.message);
                authService.logout();
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    const login = async (email, password, org) => {
        // Super Admin should not have an org context
        const isSuperAdmin = email.toLowerCase() === 'superadmin@bizlinks.in';
        const orgContext = isSuperAdmin ? null : (org || localStorage.getItem('orgContext'));

        const response = await authService.login(email, password, orgContext);
        setUser(response.data.user);
        return response;
    };

    const logout = () => {
        authService.logout();
        sessionStorage.removeItem('activeRole');
        setUser(null);
    };

    const registerOrganization = async (data) => {
        return await authService.registerOrganization(data);
    };

    const registerUser = async (data) => {
        return await authService.registerUser(data);
    };

    const updateUser = (data) => {
        const updatedUser = { ...user, ...data };
        setUser(updatedUser);
        sessionStorage.setItem('user', JSON.stringify(updatedUser));

        // Update organization context if organization info changed
        if (data.organizationName || data.subDomain) {
            const orgContext = data.subDomain || data.organizationName || updatedUser.subDomain || updatedUser.organizationName;
            if (orgContext) {
                localStorage.setItem('orgContext', orgContext);
            }
        }
    };

    const refreshUser = async () => {
        try {
            const response = await api.get('/users/profile');
            if (response.data.success) {
                const freshUser = response.data.data;
                setUser(freshUser);
                sessionStorage.setItem('user', JSON.stringify(freshUser));
                return freshUser;
            }
        } catch (error) {
            console.error('Failed to refresh user data:', error);
            throw error;
        }
    };

    return (
        <AuthContext.Provider value={{ user, login, logout, registerOrganization, registerUser, updateUser, refreshUser, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

